import { Dialog, Transition } from "@headlessui/react";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Close } from "@/assets/icons/ds";
import { BurgerMenu, LogoWordmark } from "@/assets/icons/icons";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { LanguageMenu } from "@/components/Layout/BaseLayouts/Sidebar/Navigation/LanguageMenu";
import { NavPanelContent } from "@/components/Layout/BaseLayouts/Sidebar/Navigation/NavPanelContent";
import { breakpoints } from "@/styles/screens";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

/**
 * Burger menu button + slide-in drawer for mobile/tablet.
 * `floating` mirrors Header's atTop state to keep the toggle button aligned with it.
 *
 * The open button stays in the Header DOM (right after the logo) so keyboard
 * tab order matches the visual top bar. It used to portal to `document.body`,
 * which pushed it to the last tab stop.
 */
export const BurgerDrawer = ({ floating }: { floating?: boolean }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  // Close the drawer if resized past the desktop breakpoint (button is
  // desktop:hidden, so it can't be toggled shut).
  useEffect(() => {
    if (!open) {
      return;
    }

    const query = window.matchMedia(`(min-width: ${breakpoints.desktop}px)`);
    if (query.matches) {
      close();

      return;
    }

    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        close();
      }
    };
    query.addEventListener("change", handleChange);

    return () => query.removeEventListener("change", handleChange);
  }, [open]);

  return (
    <>
      {/* Stays mounted while open (the panel covers it) so Dialog can restore focus to it. */}
      <button
        className={clsx(
          "group relative z-50 flex h-13 items-center justify-center rounded-md p-2 outline-none transition-[top,right] duration-300 ease-in-out hover:bg-surface-tertiary desktop:hidden",
          // Floating header: match its 8px inset to stay inside the bar.
          floating ? "fixed right-6 top-2" : "fixed right-4 top-0",
        )}
        type="button"
        tabIndex={KEYBOARD_TAB_INDEX}
        aria-label={t("layout.menu.burger.open")}
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <FocusRing />
        <BurgerMenu className="size-6 [&>path]:fill-foreground-primary" />
      </button>

      <Transition as="div" show={open}>
        {/* Named on the Dialog: it carries role="dialog", the Panel is a plain div. */}
        <Dialog
          onClose={close}
          aria-label={t("portal:layout.menu.sidebar.ariaLabel")}
        >
          <Transition.Child
            as="div"
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 z-40 bg-surface-primary/40"
              aria-hidden="true"
            />
          </Transition.Child>

          {/* Positioning classes go on this transform-animated element, not a nested Dialog.Panel -- see LanguageMenu.tsx for the same containing-block issue. */}
          <Transition.Child
            as="div"
            className="fixed inset-y-0 right-0 z-40 flex w-full tablet:w-[calc(100%-32px)] tablet:max-w-sm"
            enter="ease-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel
              data-testid="burger-drawer-panel"
              className="flex w-full flex-col overflow-y-auto tablet:p-2"
            >
              <div className="flex flex-1 flex-col gap-gutter bg-background-secondary p-5 tablet:rounded-lg">
                <div className="flex items-center gap-2">
                  <LogoWordmark
                    className="h-8 w-16 text-foreground-primary"
                    role="img"
                    aria-hidden={false}
                    aria-label={t("Common.logoName")}
                  />
                  <button
                    className="group relative ml-auto flex items-center justify-center rounded-md p-2 outline-none hover:bg-surface-tertiary"
                    type="button"
                    tabIndex={KEYBOARD_TAB_INDEX}
                    aria-label={t("layout.menu.burger.close")}
                    onClick={close}
                  >
                    <FocusRing />
                    <Close className="size-6 text-foreground-primary" />
                  </button>
                </div>

                <NavPanelContent onNavigate={close} />

                <div className="mt-auto">
                  <LanguageMenu />
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>
    </>
  );
};
